import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Layout } from "@/components/Layout";
import { RBACGuard } from "@/components/RBACGuard";
import { AlertsPage } from "@/pages/AlertsPage";
import { AuditLogPage } from "@/pages/AuditLogPage";
import { CameraDetailPage } from "@/pages/CameraDetailPage";
import { CameraGridPage } from "@/pages/CameraGridPage";
import { CameraListPage } from "@/pages/CameraListPage";
import { TopologyPage } from "@/pages/TopologyPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { AssetRegisterPage } from "@/pages/AssetRegisterPage";
import EMapView from "@/pages/Map/EMapView";
import { CMSDashboard } from "@/pages/CMSDashboard";
import { AdminCamerasPage } from "@/pages/AdminCamerasPage";
import EntityBrowserPage from "@/pages/EntityBrowserPage";
import EntityTypeManagerPage from "@/pages/EntityTypeManagerPage";
import { EquipmentHealthPage } from "@/pages/EquipmentHealthPage";
import TenantListPage from "@/pages/SuperAdmin/TenantList";
import TenantCreatePage from "@/pages/SuperAdmin/TenantCreate";
import TenantDetailPage from "@/pages/SuperAdmin/TenantDetail";
import TenantSettingsPage from "@/pages/Settings/TenantSettings";
import BulkImportPage from "@/pages/Entities/BulkImport";
import MapViewPage from "@/pages/Map/MapView";
import UserInvitePage from "@/pages/Users/UserInvite";
import UserDetailPage from "@/pages/Users/UserDetail";
import AcceptInvitePage from "@/pages/Users/AcceptInvite";
import RoleListPage from "@/pages/Roles/RoleList";
import RoleEditorPage from "@/pages/Roles/RoleEditor";
import MFASettingsPage from "@/pages/Settings/MFASettings";
import SessionManagerPage from "@/pages/Settings/SessionManager";
import SSOConfigPage from "@/pages/Settings/SSOConfig";
import APITokensPage from "@/pages/Settings/APITokens";
import ChangePasswordPage from "@/pages/Settings/ChangePassword";
// S5 — device registry pages.
import ProfileManagerPage from "@/pages/Devices/ProfileManager";
import BulkImportCamerasPage from "@/pages/Devices/BulkImport";
import CameraWizardPage from "@/pages/Devices/CameraWizard";
// S6 — ONVIF discovery page.
import DiscoveryPage from "@/pages/Devices/Discovery";
// U1c-3 — SLink edge-agent provisioning (list + add).
import SlinkAgentListPage from "@/pages/Devices/SlinkAgentList";
import SlinkAgentCreatePage from "@/pages/Devices/SlinkAgentCreate";
// S7 — saved camera layouts.
import LayoutManagerPage from "@/pages/Layouts/LayoutManager";
// S9 — health monitoring dashboard.
import HealthDashboardPage from "@/pages/Health/HealthDashboard";
import CameraHealthDetailPage from "@/pages/Health/CameraHealthDetail";
// S12 — evidence + reports pages.
import EvidenceCenter from "@/pages/Evidence/EvidenceCenter";
import CaseDetailPage from "@/pages/Evidence/CaseDetailPage";
import ReportBuilder from "@/pages/Reports/ReportBuilder";
// S13a — notifications settings page.
import NotificationsSettings from "@/pages/Settings/NotificationsSettings";
// S13b — live-view transport settings.
import LiveViewTransportSettings from "@/pages/Settings/LiveViewTransport";
// S13c — edge auth migration dashboard.
import EdgeAuthMigration from "@/pages/Settings/EdgeAuthMigration";
// S14 — storage + retention settings.
import StorageSettings from "@/pages/Settings/StorageSettings";
// S15 — SInsight foundation: detections list.
import DetectionsPage from "@/pages/Analytics/DetectionsPage";
// S16 — SInsight after-hours: schedule + module config.
import AnalyticsConfigPage from "@/pages/Analytics/AnalyticsConfigPage";
// S16-batch — SInsight zone/line shape drawing.
import ZonesPage from "@/pages/Analytics/ZonesPage";
// S16-fire — assistive fire/smoke config.
import FireConfigPage from "@/pages/Analytics/FireConfigPage";
// S17A — SOP/runbook engine + escalation matrix (admin).
import RunbookManager from "@/pages/Admin/RunbookManager";
import EscalationMatrix from "@/pages/Admin/EscalationMatrix";
// S17B — licensing & usage.
import { LicensingPage } from "@/pages/LicensingPage";
import { AccessDenied } from "@/components/AccessDenied";
import { useTenantStore } from "@/store/tenant";

function SuperAdminGuard({ children }: { children: JSX.Element }) {
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  // Was a SILENT `<Navigate to="/dashboard">`. An operator clicked
  // "Admin · Tenants", landed on the Dashboard, and could not tell whether the
  // link was broken, the page was empty, or they were not allowed in. This
  // renders INSIDE the Layout outlet, so the sidebar and nav stay put.
  //
  // Note `isSuperAdmin` defaults to FALSE in the store, so on a hard navigation
  // straight to this URL a genuine super-admin can see this page for a moment
  // before the store populates — a named "no access" that resolves is still far
  // better than a redirect that silently discards where they were going.
  if (!isSuperAdmin) {
    return <AccessDenied what="Tenant administration" needs="super-admin" />;
  }
  return children;
}
import { LicensePage } from "@/pages/LicensePage";
import { LiveView } from "@/pages/LiveView";
import { LoginPage } from "@/pages/LoginPage";
import { RecordingsPage } from "@/pages/RecordingsPage";
import { SiteDetailPage } from "@/pages/SiteDetailPage";
import { SitesListPage } from "@/pages/SitesListPage";
import { UsersPage } from "@/pages/UsersPage";

// The client lives in lib/queryClient.ts so the session code can clear it.

export function App() {
  return (
    // Root boundary. The Outlet boundary inside Layout catches page crashes
    // while keeping the chrome; a throw in Layout ITSELF (or in RBACGuard, or
    // in the router) is ABOVE that one, so without this the white screen is
    // back for exactly the component that wraps everything. Belt and braces,
    // and the braces are the ones that were missing.
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary scope="root">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Public route — invitation acceptance is pre-auth */}
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route
            element={
              <RBACGuard>
                <Layout />
              </RBACGuard>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="sites" element={<SitesListPage />} />
            <Route path="sites/:id" element={<SiteDetailPage />} />
            <Route path="assets" element={<AssetRegisterPage />} />
            <Route path="cameras" element={<CameraListPage />} />
            {/* S5 — literal /cameras/add and /cameras/import paths
                registered BEFORE /cameras/:id so they're not caught
                by the parameterized route. */}
            <Route path="cameras/add" element={<CameraWizardPage />} />
            <Route
              path="cameras/import"
              element={<BulkImportCamerasPage />}
            />
            <Route
              path="devices/profiles"
              element={<ProfileManagerPage />}
            />
            <Route
              path="devices/discovery"
              element={<DiscoveryPage />}
            />
            <Route path="devices/slink" element={<SlinkAgentListPage />} />
            <Route
              path="devices/slink/new"
              element={<SlinkAgentCreatePage />}
            />
            <Route path="cameras/:id" element={<CameraDetailPage />} />
            <Route path="grid" element={<CameraGridPage />} />
            <Route path="topology" element={<TopologyPage />} />
            <Route path="live" element={<LiveView />} />
            <Route path="layouts" element={<LayoutManagerPage />} />
            <Route path="health" element={<HealthDashboardPage />} />
            <Route
              path="health/cameras/:cameraId"
              element={<CameraHealthDetailPage />}
            />
            <Route path="equipment-health" element={<EquipmentHealthPage />} />
            <Route path="entities" element={<EntityBrowserPage />} />
            <Route
              path="entities/bulk-import"
              element={<BulkImportPage />}
            />
            <Route path="map" element={<MapViewPage />} />
            <Route path="emap" element={<EMapView />} />
            <Route path="cms" element={<CMSDashboard />} />
            <Route path="settings/entity-types" element={<EntityTypeManagerPage />} />
            <Route path="settings/tenant" element={<TenantSettingsPage />} />
            <Route
              path="admin/tenants"
              element={<SuperAdminGuard><TenantListPage /></SuperAdminGuard>}
            />
            <Route
              path="admin/tenants/create"
              element={<SuperAdminGuard><TenantCreatePage /></SuperAdminGuard>}
            />
            <Route
              path="admin/tenants/:id"
              element={<SuperAdminGuard><TenantDetailPage /></SuperAdminGuard>}
            />
            <Route path="recordings" element={<RecordingsPage />} />
            <Route path="admin/cameras" element={<AdminCamerasPage />} />
            {/* S17A — SOP/runbook + escalation matrix (admin). */}
            <Route path="admin/runbooks" element={<RunbookManager />} />
            <Route path="admin/escalation" element={<EscalationMatrix />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/invite" element={<UserInvitePage />} />
            <Route path="users/:id" element={<UserDetailPage />} />
            <Route path="roles" element={<RoleListPage />} />
            <Route path="roles/create" element={<RoleEditorPage />} />
            <Route path="roles/:id/edit" element={<RoleEditorPage />} />
            <Route path="settings/mfa" element={<MFASettingsPage />} />
            <Route path="settings/sessions" element={<SessionManagerPage />} />
            <Route path="settings/api-tokens" element={<APITokensPage />} />
            <Route path="settings/change-password" element={<ChangePasswordPage />} />
            <Route path="settings/sso" element={<SSOConfigPage />} />
            <Route path="settings/notifications" element={<NotificationsSettings />} />
            <Route path="settings/live-view" element={<LiveViewTransportSettings />} />
            <Route path="settings/edge-auth" element={<EdgeAuthMigration />} />
            <Route path="settings/storage" element={<StorageSettings />} />
            <Route path="analytics/detections" element={<DetectionsPage />} />
            <Route path="analytics/config" element={<AnalyticsConfigPage />} />
            <Route path="analytics/zones" element={<ZonesPage />} />
            <Route path="analytics/fire" element={<FireConfigPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            {/* S12 — evidence + reports. */}
            <Route path="evidence" element={<EvidenceCenter />} />
            <Route path="evidence/cases/:id" element={<CaseDetailPage />} />
            <Route path="reports" element={<ReportBuilder />} />
            <Route path="license" element={<LicensePage />} />
            <Route path="licensing" element={<LicensingPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
